import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, FileSignature, Eraser, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { CONTACT, IMAGES } from "../mock";
import Seo from "../components/Seo";

// Google Form integration — every signed waiver is mirrored to the
// linked Google Sheet for searchable storage.
const GOOGLE_FORM_ACTION =
  "https://docs.google.com/forms/d/e/1FAIpQLSdah2WWhjrFdysuQ1utaawJjcQugK3PgYLNzi1ukFzPLI3Mgw/formResponse";
const GFORM_FIELDS = {
  firstName: "entry.603950369",
  lastName: "entry.718935160",
  dob: "entry.1170863267", // date field — uses _year/_month/_day
  phone: "entry.420133436",
  email: "entry.1913899099",
  emergencyContact: "entry.471693739",
  guardianFullName: "entry.1939376331",
  guardianRelationship: "entry.1481259484",
  guardianPhone: "entry.1413773142",
  typedSignature: "entry.2123676572",
};

const WAIVER_PARAGRAPHS = [
  "I am the participant or the parent/legal guardian of a minor participant (where applicable), hereby agree to the following terms and conditions:",
  "1. I accept and agree that this release of liability, voluntary assumption of risk, indemnification, and waiver of claim (hereinafter, the \"Release\") shall be binding on me and/or the minor participant, and my or the minor participant's heirs, representatives, endeavors, next of kin, beneficiaries, administrators, successors, and assigns.",
  "2. I, on behalf of myself or on behalf of the minor participant do agree that Hang Zen Surf School, it's owners, agents, employees, independent contractors, and sponsors along with the City of Encinitas, their elected officials, officer, agents employees and volunteers (hereinafter, the \"Released Parties\") shall not be liable for any damages arising from personal injuries (including death), damage to or loss of property or other harm, whether foreseen or unforeseen, present or future, known or unknown that I or the minor participant may sustain in any way which directly or indirectly results from or arises out of my or minor participant's participation in surf lessons/instructions conducted by Hang Zen Surf School or any of the Released Parties.",
  "3. I am aware that surfing involves many inherent risks, dangers and hazards, including but not limited to the use and operation of surfboards, impact of collision with surfboards of any thereof including the fins and other attachments; skin irritations or abrasions caused by the use of wetsuits; ear infections caused by the water; impact to the beachhead, or as the case may be, the ocean floor; severe weather conditions; my ability to swim in dangerous ocean conditions, as the case may be; extreme water and air temperatures; the prevailing condition of the surf; undertow and tide; the possible existence of dangerous rocks, reefs, driftwood, logs, floating objects, sea mammals and other hazards and obstructions, whether marked or unmarked in the area chosen by you to conduct surf camps; impact of collision with swimmers or other surfers or their surfboards, whether or not caused by negligence of such swimmers or surfer; dangers involved in traveling to the surfing area including possible transportation provided by Hang Zen Surf School; I am also advised that the bluffs and sea cliffs along the beach may collapse or fall suddenly and unpredictably and can cause injury or death (collectively referred to as the \"Surfing Activities\"). I freely accept and fully assume all such risks, dangers, and hazards.",
  "4. Despite my understanding of the foregoing risks, I, individually and as the parent or legal guardian of the minor participant, AGREE NOT TO SUE AND TO RELEASE FROM LIABILITY AND TO DEFEND, INDEMNIFY AND HOLD HARMLESS THE RELEASED PARTIES for any damage or injury arising out of mine or the minor child's participation in the Surfing Activities regardless of the cause, including NEGLIGENCE.",
  "5. I, on my own behalf and/or on behalf of the minor participant, further agree to and do hereby expressly, knowingly, and voluntarily assume any and all risks involved in the Surfing Activities and/or loss of my or the minor participant's property, real or personal, caused by or arising out of my or the minor participant's involvement in the Surfing Activities. I, on my own behalf and/or on behalf of the minor participant, hereby represent and certify that I and certify that I am, or the minor participant is in good health and has no mental or physical condition that would prevent my or the minor participant's participation in the Surfing Activities.",
  "6. I, on my own behalf and/or on behalf of the minor participant, understand that THIS RELEASE, INCLUDES ANY CLAIMS BASED IN WHOLE OR IN PART ON THE NEGLIGENCE, ACTION OR INACTION OF ANY OF THE RELEASED PARTIES AND HEREBY RELEASE, WAIVE AND FULLY DISCHARGE THE RELEASED PARTIES FROM ANY AND ALL LIABILITY FOR ANY CLAIMS, EVEN IF CAUSED IN WHOLE OR IN PART BY THE PRESENT OR FUTURE NEGLIGENCE, FAULT, STRICT PRODUCT LIABILITY, BREACH OF CONTRACT OR OTHER ACT, CONDUCT OR STATUS OF ANY OF THE RELEASED PARTIES.",
  "BY SIGNING THIS RELEASE, I ON BEHALF OF MYSELF OR ON THE BEHALF OF THE MINOR PARTICIPANT UNDERSTAND THAT I HAVE WAIVED MY (OR THE MINOR PARTICIPANT') RIGHT TO SUE THE RELEASED PARTIES FOR ANY INJURY RESULTING FROM SURF INSTRUCTIONS OR THE SURF ACTIVITIES DISCUSSED HEREIN.",
  "I have read and understand this Release and Indemnity Agreement prior to signing it and am aware that by signing this document, I am affecting the legal rights and liabilities of myself, my heirs, next to kin, executors, administrators and assigns in relation to the aforementioned activities. This Release must be signed by the participant or participant's legal guardian and received by Hang Zen Surf School before participation is permitted.",
];

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-sm font-semibold text-slate-700 mb-1.5">
      {label} {required && <span className="text-orange-600">*</span>}
    </span>
    {children}
  </label>
);

const inputCls =
  "w-full px-4 py-2.5 rounded-lg border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#0b3a5f] focus:border-transparent transition";

const SignaturePad = ({ value, onChange }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const dirtyRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2.2;
      ctx.strokeStyle = "#0b3a5f";
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPos(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    dirtyRef.current = true;
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (dirtyRef.current) {
      const data = canvasRef.current.toDataURL("image/png");
      onChange(data);
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    dirtyRef.current = false;
    onChange("");
  };

  return (
    <div>
      <div className="relative rounded-xl border-2 border-dashed border-slate-300 bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          data-testid="waiver-signature-canvas"
          className="w-full h-44 touch-none cursor-crosshair block"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
        {!value && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-slate-400 text-sm">
            Sign with your finger or mouse here
          </div>
        )}
      </div>
      <div className="flex justify-between items-center mt-2">
        <span className="text-xs text-slate-500">
          By signing above you acknowledge and authorize this as your digital signature.
        </span>
        <button
          type="button"
          onClick={clear}
          data-testid="waiver-signature-clear"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-orange-600 transition-colors"
        >
          <Eraser size={14} /> Clear
        </button>
      </div>
    </div>
  );
};

const initialForm = {
  participantLastName: "",
  participantFirstName: "",
  participantDob: "",
  participantPhone: "",
  participantEmail: "",
  emergencyContact: "",
  emergencyPhone: "",
  emergencyPhoneAlt: "",
  guardianFirstName: "",
  guardianLastName: "",
  guardianRelationship: "",
  guardianPhone: "",
  confirmType: "", // "adult" or "guardian"
  authorize: false,
  signature: "",
};

const Waiver = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const setConfirm = (val) => setForm((f) => ({ ...f, confirmType: val }));

  const isGuardian = form.confirmType === "guardian";

  const validate = () => {
    if (!form.participantFirstName.trim() || !form.participantLastName.trim())
      return "Please enter the participant's first and last name.";
    if (!form.participantDob) return "Please enter the participant's date of birth.";
    if (!form.participantPhone.trim()) return "Please enter a phone number.";
    if (!form.confirmType) return "Please confirm whether you are the participant or the legal guardian.";
    if (isGuardian && (!form.guardianFirstName.trim() || !form.guardianLastName.trim()))
      return "Please enter the legal guardian's first and last name.";
    if (!form.authorize) return "Please check the authorization box to submit.";
    if (!form.signature) return "Please sign in the signature box above.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSending(true);
    try {
      const fullName = `${form.participantFirstName} ${form.participantLastName}`.trim();
      const guardianFullName = isGuardian
        ? `${form.guardianFirstName} ${form.guardianLastName}`.trim()
        : "";
      const emergencyFull = [form.emergencyContact, form.emergencyPhone, form.emergencyPhoneAlt]
        .filter(Boolean)
        .join(" · ");

      // 1) Mirror submission into the linked Google Form / Sheet for searchable storage.
      //    Google Forms doesn't return CORS headers — fire-and-forget via no-cors.
      try {
        const gf = new URLSearchParams();
        gf.set(GFORM_FIELDS.firstName, form.participantFirstName);
        gf.set(GFORM_FIELDS.lastName, form.participantLastName);
        gf.set(GFORM_FIELDS.phone, form.participantPhone);
        gf.set(GFORM_FIELDS.email, form.participantEmail);
        gf.set(GFORM_FIELDS.emergencyContact, emergencyFull);
        gf.set(GFORM_FIELDS.guardianFullName, guardianFullName || "N/A (adult signer)");
        gf.set(GFORM_FIELDS.guardianRelationship, isGuardian ? form.guardianRelationship : "N/A");
        gf.set(GFORM_FIELDS.guardianPhone, isGuardian ? form.guardianPhone : "N/A");
        // Typed-signature field on the Google Form — use the signer's full legal name.
        gf.set(GFORM_FIELDS.typedSignature, isGuardian ? guardianFullName : fullName);
        // Date field needs to be sent as three separate _year/_month/_day params.
        if (form.participantDob) {
          const [y, m, d] = form.participantDob.split("-");
          gf.set(`${GFORM_FIELDS.dob}_year`, y);
          gf.set(`${GFORM_FIELDS.dob}_month`, String(parseInt(m, 10)));
          gf.set(`${GFORM_FIELDS.dob}_day`, String(parseInt(d, 10)));
        }
        await fetch(GOOGLE_FORM_ACTION, {
          method: "POST",
          mode: "no-cors",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: gf.toString(),
        });
      } catch (_gfErr) {
        // never block the user on Google Form failure — email below is the durable copy
      }

      // 2) Email full record (including the drawn signature image) to Hang Zen Surf School.
      const payload = {
        _subject: `Signed Waiver — ${fullName}`,
        _template: "table",
        _captcha: "false",
        "Participant Name": fullName,
        "Date of Birth": form.participantDob,
        "Phone": form.participantPhone,
        "Email": form.participantEmail,
        "Emergency Contact": form.emergencyContact,
        "Emergency Phone": form.emergencyPhone,
        "Emergency Phone (Alt)": form.emergencyPhoneAlt,
        "Signed As": isGuardian ? "Legal Guardian of Minor" : "Participant (18+)",
        "Guardian Name": guardianFullName,
        "Guardian Relationship": isGuardian ? form.guardianRelationship : "",
        "Guardian Phone": isGuardian ? form.guardianPhone : "",
        "Digital Signature Authorization": "YES — authorized as digital signature",
        "Signature Image (data URL)": form.signature,
        "Signed At": new Date().toISOString(),
      };
      const res = await fetch("https://formsubmit.co/ajax/hangzensurfschool@gmail.com", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok || data.success === "true" || data.success === true) {
        const stored = JSON.parse(localStorage.getItem("hz_waivers") || "[]");
        stored.push({
          name: fullName,
          dob: form.participantDob,
          signedAs: isGuardian ? "guardian" : "adult",
          at: new Date().toISOString(),
        });
        localStorage.setItem("hz_waivers", JSON.stringify(stored));
        setSent(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        throw new Error(data.message || "Submission failed");
      }
    } catch (err) {
      setError(
        `Couldn't send right now. Please call ${CONTACT.phone} or email ${CONTACT.email}.`
      );
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-stone-50 min-h-screen pt-32 pb-20">
        <Seo
          title="Waiver Signed | Hang Zen Surf + Sea"
          description="Thank you — your Hang Zen Surf School waiver has been signed and submitted."
          path="/waiver"
        />
        <div className="max-w-2xl mx-auto px-6 text-center" data-testid="waiver-success">
          <CheckCircle2 size={64} className="text-emerald-500 mx-auto mb-6" />
          <h1
            className="text-4xl md:text-5xl text-[#0b3a5f] mb-4"
            style={{ fontFamily: "'Pacifico', cursive" }}
          >
            Waiver Signed
          </h1>
          <p className="text-slate-700 text-lg mb-8">
            Thanks, {form.participantFirstName}! Your signed waiver has been
            sent to Hang Zen Surf School. We&apos;ll see you in the water.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link
              to="/book-today"
              className="inline-flex items-center px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full transition-colors"
              data-testid="waiver-success-book-cta"
            >
              Book Your Session
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-300 text-slate-800 hover:border-[#0b3a5f] hover:text-[#0b3a5f] font-semibold rounded-full transition-colors"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-stone-50">
      <Seo
        title="Surf Waiver & Release of Liability | Hang Zen Surf School"
        description="Required liability waiver for all Hang Zen Surf School participants. Read the release and sign digitally on any device."
        path="/waiver"
        image={IMAGES.hzGroup2}
      />

      {/* Hero */}
      <section className="relative h-[40vh] min-h-[320px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover"
          style={{
            backgroundImage: `url(${IMAGES.hzGroup2})`,
            backgroundPosition: "center 30%",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900/85" />
        <div className="relative z-10 h-full flex flex-col justify-end pb-10 max-w-5xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/30 text-white text-xs font-semibold uppercase tracking-[0.25em] mb-3 w-fit">
            <FileSignature size={14} /> Surf Waivers
          </div>
          <h1
            className="text-4xl md:text-6xl text-white drop-shadow-2xl mb-2"
            style={{ fontFamily: "'Pacifico', cursive" }}
          >
            Sign Your Waiver
          </h1>
          <p className="text-white/85 max-w-2xl">
            Waivers are required for all participants. For students under 18 a
            parent or legal guardian must complete the waiver.
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 md:p-10">
            <h2
              className="text-2xl md:text-3xl text-[#0b3a5f] mb-2"
              style={{ fontFamily: "'Pacifico', cursive" }}
            >
              Hang Zen Surf School — Release of Liability
            </h2>
            <div className="h-1 w-16 bg-orange-500 rounded-full mb-6" />

            <div
              className="space-y-4 text-[15px] leading-relaxed text-slate-700 max-h-[420px] overflow-y-auto pr-3 border border-slate-200 rounded-xl p-5 bg-stone-50"
              data-testid="waiver-text-scroll"
            >
              {WAIVER_PARAGRAPHS.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <form onSubmit={onSubmit} className="mt-8 space-y-8" noValidate>
              {/* Student Info */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-[0.25em] text-orange-600 mb-4">
                  Student Info
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="First Name" required>
                    <input
                      name="participantFirstName"
                      value={form.participantFirstName}
                      onChange={onChange}
                      className={inputCls}
                      data-testid="waiver-first-name"
                      autoComplete="given-name"
                    />
                  </Field>
                  <Field label="Last Name" required>
                    <input
                      name="participantLastName"
                      value={form.participantLastName}
                      onChange={onChange}
                      className={inputCls}
                      data-testid="waiver-last-name"
                      autoComplete="family-name"
                    />
                  </Field>
                  <Field label="Participant's DOB" required>
                    <input
                      type="date"
                      name="participantDob"
                      value={form.participantDob}
                      onChange={onChange}
                      className={inputCls}
                      data-testid="waiver-dob"
                    />
                  </Field>
                  <Field label="Phone" required>
                    <input
                      type="tel"
                      name="participantPhone"
                      value={form.participantPhone}
                      onChange={onChange}
                      className={inputCls}
                      data-testid="waiver-phone"
                      autoComplete="tel"
                    />
                  </Field>
                  <Field label="Email">
                    <input
                      type="email"
                      name="participantEmail"
                      value={form.participantEmail}
                      onChange={onChange}
                      className={inputCls}
                      data-testid="waiver-email"
                      autoComplete="email"
                    />
                  </Field>
                  <Field label="Emergency Contact">
                    <input
                      name="emergencyContact"
                      value={form.emergencyContact}
                      onChange={onChange}
                      className={inputCls}
                      data-testid="waiver-emergency-contact"
                    />
                  </Field>
                  <Field label="Emergency Phone">
                    <input
                      type="tel"
                      name="emergencyPhone"
                      value={form.emergencyPhone}
                      onChange={onChange}
                      className={inputCls}
                      data-testid="waiver-emergency-phone"
                    />
                  </Field>
                  <Field label="Emergency Phone (Alt)">
                    <input
                      type="tel"
                      name="emergencyPhoneAlt"
                      value={form.emergencyPhoneAlt}
                      onChange={onChange}
                      className={inputCls}
                      data-testid="waiver-emergency-phone-alt"
                    />
                  </Field>
                </div>
              </div>

              {/* Confirm Type */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-[0.25em] text-orange-600 mb-4">
                  Authorized Confirmation <span className="text-orange-600">*</span>
                </h3>
                <div className="space-y-3">
                  <label
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                      form.confirmType === "adult"
                        ? "border-[#0b3a5f] bg-[#0b3a5f]/5"
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <input
                      type="radio"
                      name="confirmType"
                      value="adult"
                      checked={form.confirmType === "adult"}
                      onChange={() => setConfirm("adult")}
                      className="mt-1"
                      data-testid="waiver-confirm-adult"
                    />
                    <span className="text-sm text-slate-700">
                      I confirm that I am of legal age (18 or older) and agree
                      to the terms of this waiver.
                    </span>
                  </label>
                  <label
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                      form.confirmType === "guardian"
                        ? "border-[#0b3a5f] bg-[#0b3a5f]/5"
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <input
                      type="radio"
                      name="confirmType"
                      value="guardian"
                      checked={form.confirmType === "guardian"}
                      onChange={() => setConfirm("guardian")}
                      className="mt-1"
                      data-testid="waiver-confirm-guardian"
                    />
                    <span className="text-sm text-slate-700">
                      I confirm that I am the legal guardian of the student and
                      agree to the terms of this waiver.
                    </span>
                  </label>
                </div>
              </div>

              {/* Guardian section */}
              {isGuardian && (
                <div data-testid="waiver-guardian-section">
                  <h3 className="text-sm font-bold uppercase tracking-[0.25em] text-orange-600 mb-4">
                    Legal Guardian Info <span className="font-normal text-slate-500 normal-case tracking-normal">(participant is under 18)</span>
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="First Name" required>
                      <input
                        name="guardianFirstName"
                        value={form.guardianFirstName}
                        onChange={onChange}
                        className={inputCls}
                        data-testid="waiver-guardian-first"
                      />
                    </Field>
                    <Field label="Last Name" required>
                      <input
                        name="guardianLastName"
                        value={form.guardianLastName}
                        onChange={onChange}
                        className={inputCls}
                        data-testid="waiver-guardian-last"
                      />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Relationship to Participant">
                        <input
                          name="guardianRelationship"
                          value={form.guardianRelationship}
                          onChange={onChange}
                          className={inputCls}
                          data-testid="waiver-guardian-relationship"
                          placeholder="e.g. Parent, Legal Guardian"
                        />
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <Field label="Legal Guardian Phone">
                        <input
                          type="tel"
                          name="guardianPhone"
                          value={form.guardianPhone}
                          onChange={onChange}
                          className={inputCls}
                          data-testid="waiver-guardian-phone"
                          autoComplete="tel"
                        />
                      </Field>
                    </div>
                  </div>
                </div>
              )}

              {/* Authorization */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-[0.25em] text-orange-600 mb-4">
                  Authorization
                </h3>
                <label className="flex items-start gap-3 p-4 rounded-xl border-2 border-slate-200 bg-white cursor-pointer">
                  <input
                    type="checkbox"
                    name="authorize"
                    checked={form.authorize}
                    onChange={onChange}
                    className="mt-1"
                    data-testid="waiver-authorize"
                  />
                  <span className="text-sm text-slate-700">
                    By submitting this form I am confirming that: 1. I am of
                    legal age (18 or above) or 2. I am the legal guardian of
                    the above participant and that I have read, understand,
                    and agree to the waiver above. Also by checking here I
                    acknowledge and authorize this as my digital signature.
                  </span>
                </label>
              </div>

              {/* Signature */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-[0.25em] text-orange-600 mb-4">
                  Signature <span className="text-orange-600">*</span>
                </h3>
                <SignaturePad
                  value={form.signature}
                  onChange={(s) => setForm((f) => ({ ...f, signature: s }))}
                />
              </div>

              {error && (
                <div
                  className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-4"
                  data-testid="waiver-error"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={sending}
                data-testid="waiver-submit"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold rounded-full transition-colors text-lg"
              >
                <FileSignature size={18} />
                {sending ? "Submitting Waiver…" : "Submit Signed Waiver"}
              </button>

              <p className="text-xs text-center text-slate-500">
                A copy of this signed waiver will be emailed to Hang Zen Surf
                School. Questions? Call {CONTACT.phone}.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Waiver;
